// Registry of import sources that get a dedicated SEO landing page
// (`/import-from-<slug>`), rendered by the shared `ImportLanding` template.
//
// MF-222 seeds the `browser` entries (Netscape HTML export → auto-detected on
// the import page). MF-246 will add the `tool` entries (Raindrop/Instapaper/…)
// with an explicit `importVariant`, plus the `/import-from-anywhere` hub — all
// reusing this same registry and template.

export type ImportSourceKind = 'browser' | 'tool';

export interface ImportSource {
  /** URL slug — the page lives at `/import-from-<slug>`. */
  slug: string;
  /** i18n key under `importLanding.sources.<id>` and logo basename. */
  id: string;
  kind: ImportSourceKind;
  /** Public path to the brand logo (SVG). */
  logo: string;
  /**
   * Import-page variant hint appended as `/import?variant=<x>`. Browsers omit
   * it — the Netscape parser is auto-detected from the dropped file.
   */
  importVariant?: string;
}

export const IMPORT_SOURCES: Record<string, ImportSource> = {
  chrome: { slug: 'chrome', id: 'chrome', kind: 'browser', logo: '/browsers/chrome.svg' },
  firefox: { slug: 'firefox', id: 'firefox', kind: 'browser', logo: '/browsers/firefox.svg' },
  edge: { slug: 'edge', id: 'edge', kind: 'browser', logo: '/browsers/edge.svg' },
  brave: { slug: 'brave', id: 'brave', kind: 'browser', logo: '/browsers/brave.svg' },
  vivaldi: { slug: 'vivaldi', id: 'vivaldi', kind: 'browser', logo: '/browsers/vivaldi.svg' },
  opera: { slug: 'opera', id: 'opera', kind: 'browser', logo: '/browsers/opera.svg' },
  safari: { slug: 'safari', id: 'safari', kind: 'browser', logo: '/browsers/safari.svg' },
};

/** Resolve a URL slug to its source config, or null for unknown slugs. */
export function getImportSource(slug: string | undefined): ImportSource | null {
  if (!slug) return null;
  return IMPORT_SOURCES[slug] ?? null;
}

export const BROWSER_SOURCES = Object.values(IMPORT_SOURCES).filter((s) => s.kind === 'browser');
export const TOOL_SOURCES = Object.values(IMPORT_SOURCES).filter((s) => s.kind === 'tool');
